// Modal création équipe — champs métier complets, max-w-4xl

import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../lib/axios";

type Zone = { id: number; nom: string };

type AgentOpt = {
  id: number;
  nom: string;
  prenom: string | null;
  fonction?: string | null;
  actif: boolean;
  equipeId?: number | null;
};

type ChefOpt = {
  id: number;
  nom: string;
  email: string | null;
  linkedAgentId: number;
};

const inputClass =
  "mt-1 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20";

export type ModalEquipeProps = {
  zones: Zone[];
  onClose: () => void;
  onSuccess: () => void;
  /** Si aucune zone : désactive la création et affiche le CTA zone */
  onOpenZoneCreate?: () => void;
  /** Chargement des zones en erreur (API) — même blocage que sans zones */
  zonesLoadError?: boolean;
  /** Largeur max du panneau (ex. max-w-3xl organisation terrain) */
  panelClassName?: string;
  /** Commentaire interne — persisté en `commentaireInterne` (API équipes). */
  showCommentField?: boolean;
};

const ModalEquipe: React.FC<ModalEquipeProps> = ({
  zones = [],
  onClose,
  onSuccess,
  onOpenZoneCreate,
  zonesLoadError = false,
  panelClassName = "max-w-4xl",
  showCommentField = false,
}) => {
  const [nom, setNom] = useState("");
  const [zoneId, setZoneId] = useState<number | "">("");
  const [chefId, setChefId] = useState<number | "">("");
  const [capaciteJour, setCapaciteJour] = useState<number | "">(0);
  const [actif, setActif] = useState(true);
  const [agentIds, setAgentIds] = useState<number[]>([]);
  const [agents, setAgents] = useState<AgentOpt[]>([]);
  const [chefs, setChefs] = useState<ChefOpt[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentNote, setCommentNote] = useState("");

  const safeZones = Array.isArray(zones) ? zones : [];
  const hasZones = safeZones.length > 0 && !zonesLoadError;

  useEffect(() => {
    setLoadingMeta(true);
    void (async () => {
      try {
        const [agentsRes, chefsRes] = await Promise.all([
          api.get("/agents"),
          api.get("/users/assignable-field-agents"),
        ]);
        const raw = Array.isArray(agentsRes.data) ? agentsRes.data : [];
        setAgents(
          raw.map((a: AgentOpt) => ({
            id: a.id,
            nom: a.nom,
            prenom: a.prenom ?? null,
            fonction: a.fonction ?? null,
            actif: a.actif,
            equipeId: a.equipeId ?? null,
          }))
        );
        const rawChefs = Array.isArray(chefsRes.data) ? chefsRes.data : [];
        setChefs(
          rawChefs.map((c: ChefOpt) => ({
            id: c.id,
            nom: c.nom,
            email: c.email ?? null,
            linkedAgentId: c.linkedAgentId,
          }))
        );
      } catch {
        setAgents([]);
        setChefs([]);
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const agentsSelectable = useMemo(() => {
    return agents
      .filter((a) => a.actif && a.equipeId == null)
      .sort((a, b) => `${a.nom} ${a.prenom ?? ""}`.localeCompare(`${b.nom} ${b.prenom ?? ""}`));
  }, [agents]);

  const chefsSelectable = useMemo(() => {
    return [...chefs].sort((a, b) => a.nom.localeCompare(b.nom));
  }, [chefs]);

  const selectedChef = useMemo(() => {
    return chefId === "" ? null : chefs.find((chef) => chef.id === Number(chefId)) ?? null;
  }, [chefId, chefs]);

  const selectedMemberIds = useMemo(() => {
    return new Set([
      ...agentIds,
      ...(selectedChef?.linkedAgentId != null ? [selectedChef.linkedAgentId] : []),
    ]);
  }, [agentIds, selectedChef]);

  const toggleAgent = (id: number) => {
    setAgentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasZones) {
      toast.warning("Créez d’abord au moins une zone.");
      return;
    }
    if (!nom.trim() || nom.trim().length < 2) {
      toast.error("Le nom de l’équipe doit contenir au moins 2 caractères.");
      return;
    }
    if (zoneId === "") {
      toast.error("La zone est obligatoire.");
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        nom: nom.trim(),
        zoneId: Number(zoneId),
        capaciteJour:
          capaciteJour === "" ? 0 : Math.max(0, Math.floor(Number(capaciteJour))),
        actif,
      };
      if (chefId !== "") payload.chefId = Number(chefId);
      else payload.chefId = null;
      const memberAgentIds = [...selectedMemberIds];
      if (memberAgentIds.length > 0) payload.agentIds = memberAgentIds;
      const note = commentNote.trim();
      if (note) payload.commentaireInterne = note;

      await api.post("/equipes", payload);

      toast.success("Équipe créée.");
      setNom("");
      setZoneId("");
      setChefId("");
      setCapaciteJour(0);
      setActif(true);
      setAgentIds([]);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status === 401) {
        toast.error("Session expirée. Reconnectez-vous.");
        return;
      }
      toast.error(ax.response?.data?.error || "Erreur lors de la création de l’équipe.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex min-w-0 items-end justify-center overflow-x-hidden bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-equipe-title"
        className={`relative flex max-h-[90dvh] w-full min-w-0 flex-col overflow-hidden rounded-t-2xl border border-slate-700/70 bg-slate-900 shadow-2xl sm:max-h-[min(90dvh,900px)] sm:rounded-2xl ${panelClassName}`}
      >
        <header className="relative shrink-0 border-b border-slate-800 px-4 pb-4 pt-5 pr-14 sm:px-6 sm:pb-4 sm:pt-6">
          <button
            type="button"
            className="absolute right-2 top-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>

          <h2 id="modal-equipe-title" className="break-words text-xl font-bold text-white">
            Nouvelle équipe
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            Rattachez l’équipe à une zone, définissez la capacité et affectez éventuellement un chef et des agents.
          </p>
        </header>

        {!hasZones ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm text-amber-100">
              <p className="font-medium">
                {zonesLoadError ? "Zones indisponibles" : "Aucune zone disponible"}
              </p>
              <p className="mt-1 text-amber-200/90">
                {zonesLoadError
                  ? "Le chargement des zones a échoué. La création d’équipe est désactivée tant que les zones ne sont pas disponibles."
                  : "Créez une zone géographique avant de pouvoir créer une équipe."}
              </p>
              {!zonesLoadError && onOpenZoneCreate ? (
                <button
                  type="button"
                  onClick={() => onOpenZoneCreate()}
                  className="mt-4 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-500"
                >
                  Créer une zone
                </button>
              ) : null}
            </div>
          </div>
        ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className="text-slate-400">Nom équipe *</span>
              <input
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className={inputClass}
                placeholder="Ex. Équipe Nord"
                disabled={!hasZones || submitting}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400">Zone *</span>
              <select
                value={zoneId === "" ? "" : String(zoneId)}
                onChange={(e) =>
                  setZoneId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className={inputClass}
                disabled={!hasZones || submitting}
                required={hasZones}
              >
                <option value="">— Choisir une zone —</option>
                {safeZones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.nom}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className="text-slate-400">Chef d’équipe (optionnel)</span>
              <select
                value={chefId === "" ? "" : String(chefId)}
                onChange={(e) =>
                  setChefId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className={inputClass}
                disabled={!hasZones || submitting || loadingMeta}
              >
                <option value="">— Aucun —</option>
                {chefsSelectable
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nom}
                    </option>
                  ))}
              </select>
              {!loadingMeta && chefsSelectable.length === 0 ? (
                <p className="mt-1 text-xs text-amber-300">
                  Aucun agent terrain actif avec accès plateforme n’est disponible.
                </p>
              ) : null}
            </label>
            <label className="block text-sm">
              <span className="text-slate-400">Capacité jour (passages)</span>
              <input
                type="number"
                min={0}
                value={capaciteJour === "" ? "" : capaciteJour}
                onChange={(e) => {
                  const v = e.target.value;
                  setCapaciteJour(v === "" ? "" : Math.max(0, parseInt(v, 10) || 0));
                }}
                className={inputClass}
                disabled={!hasZones || submitting}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <span className="text-sm text-slate-400">Agents à affecter (optionnel)</span>
              <p className="mb-2 text-xs text-slate-500">
                Agents terrain actifs sans équipe ; le chef sélectionné est inclus automatiquement s’il est libre.
              </p>
              <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-700 bg-slate-950/60 p-2">
                {loadingMeta ? (
                  <p className="flex items-center gap-2 px-2 py-4 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
                  </p>
                ) : agentsSelectable.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-slate-500">
                    Aucun agent terrain actif sans équipe n’est disponible.
                  </p>
                ) : (
                  agentsSelectable.map((a) => (
                    (() => {
                      const isChefMember = selectedChef?.linkedAgentId === a.id;
                      return (
                    <label
                      key={a.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-800/80"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.has(a.id)}
                        onChange={() => toggleAgent(a.id)}
                        disabled={!hasZones || submitting || isChefMember}
                        className="rounded border-slate-600"
                      />
                      <span>
                        {[a.prenom, a.nom].filter(Boolean).join(" ")}
                        {a.fonction ? (
                          <span className="text-slate-500"> · {a.fonction}</span>
                        ) : null}
                        {isChefMember ? (
                          <span className="text-emerald-300"> · chef d’équipe</span>
                        ) : null}
                      </span>
                    </label>
                      );
                    })()
                  ))
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={actif}
                onChange={(e) => setActif(e.target.checked)}
                disabled={!hasZones || submitting}
                className="rounded border-slate-600"
              />
              Équipe active
            </label>

            {showCommentField ? (
              <label className="block text-sm">
                <span className="text-slate-400">Commentaire interne</span>
                <textarea
                  value={commentNote}
                  onChange={(e) => setCommentNote(e.target.value)}
                  rows={2}
                  className={inputClass}
                  placeholder="Note interne pour les équipes terrain et le pilotage."
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Enregistrée avec l’équipe ; visible côté administration.
                </p>
              </label>
            ) : null}
          </div>
          </div>

          <footer className="relative z-10 shrink-0 border-t border-slate-800 bg-slate-950/95 p-4 backdrop-blur-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="w-full rounded-xl border border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 disabled:opacity-50 sm:w-auto min-h-11"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting || !hasZones}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 sm:w-auto sm:min-w-[11rem]"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Créer l’équipe
              </button>
            </div>
          </footer>
        </form>
        )}
      </div>
    </div>
  );
};

export default ModalEquipe;
